// src/features/tasks/components/TaskActionButtons.jsx
import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  ButtonGroup,
  Menu,
  Typography, 
  IconButton, 
  Tooltip, 
  Divider,
  MenuItem, 
  ListItemIcon, 
  ListItemText 
} from '@mui/material';
import { getAvailableTransitions } from '../utils/taskStateUtils';
import { useTranslation } from 'react-i18next';
import { useTaskPermissions } from '../../auth/hooks/usePermissions';
import { ArrowDropDown } from '@mui/icons-material';

/**
 * Maps task actions to their required permission
 */
const ACTION_PERMISSION_MAP = {
  start: 'canStart',
  hold: 'canHold',
  resume_pending: 'canResume',
  resume_progress: 'canResume',
  complete: 'canComplete',
  not_complete: 'canComplete',
  cancel: 'canCancel',
  reopen: 'canReopen',
};

/**
 * TaskActionButtons - Renders action buttons for task state transitions
 *
 * @param {Object} task - The task object (or row object)
 * @param {Function} onStateChange - Callback when a state transition is triggered (task, action) or (action)
 * @param {boolean} loading - Whether the component is in a loading state
 * @param {number} maxVisibleButtons - Maximum number of buttons to show (default: 3)
 * @param {string} variant - Display variant: 'buttons' (default), 'icons', 'card', or 'menuItems'
 * @param {string} buttonVariant - MUI Button variant for the 'card' variant (default: 'outlined')
 * @param {string} size - Button size: 'small', 'medium' (default), or 'large'
 * @param {Function} onClick - Additional onClick handler for event handling (e.g., stopPropagation)
 * @param {Object} sx - Additional sx props for the container
 */
export default function TaskActionButtons({
  task,
  onStateChange,
  loading = false,
  maxVisibleButtons = 3,
  variant = 'buttons',
  buttonVariant = 'outlined', // 'card' variant only
  size = 'medium',
  onClick,
  sx = {}
}) {
  const { t } = useTranslation(['common', 'tasks']);
  const taskPermissions = useTaskPermissions();


  // Get all available transitions based on task state
  const stateTransitions = getAvailableTransitions(task);

  // Filter transitions based on user permissions
  const availableTransitions = stateTransitions.filter((transition) => {
    const permissionKey = ACTION_PERMISSION_MAP[transition.action];
    return permissionKey && taskPermissions[permissionKey];
  });

  // Default rendering hooks
  const [anchorEl, setAnchorEl] = useState(null);

  if (!availableTransitions.length) return null;

  const primary = availableTransitions[0];
  const secondary = availableTransitions.slice(1);

  const PrimaryIcon = primary.icon;

  const handleMainClick = (e) => {
    handleAction(primary, e);
  };

  const handleToggleMenu = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (!task) return null;

  // No actions message (only for 'buttons' variant)
  if (!onStateChange || availableTransitions.length === 0) {
    if (variant === 'menuItems') return null;
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        {!onStateChange
          ? t('tasks:messages.no_permission_state')
          : t('tasks:messages.no_actions_available')
        }
      </Typography>
    );
  }

  const handleAction = (transition, event) => {
    if (onClick) {
      onClick(event);
    }
    // Support both callback signatures: (task, action) or (action)
    if (onStateChange.length === 2) {
      onStateChange(task, transition.action);
    } else {
      onStateChange(transition.action);
    }
  };

  // Check if complete button should be disabled due to uncompleted procedures or dual signature
  const isCompleteDisabled = (transition) => {
    if (transition.action !== 'complete') return false;

    // Check for uncompleted procedures
    if (task.has_uncompleted_procedure_executions) return true;

    // Check for incomplete dual signature receipt
    if (task.requires_dual_signature && task.completion_receipt_status !== 'COMPLETE') return true;

    return false;
  };

  // Get tooltip message for disabled complete button
  const getCompleteDisabledMessage = (transition) => {
    if (transition.action !== 'complete') return '';

    if (task.has_uncompleted_procedure_executions) {
      return t('tasks:messages.complete_procedures_first');
    }

    if (task.requires_dual_signature && task.completion_receipt_status !== 'COMPLETE') {
      return t('tasks:messages.dual_signature_required', { defaultValue: 'Dual signature receipt must be completed first' });
    }

    return '';
  };

  // Render as icon buttons (for data grids)
  if (variant === 'icons') {
    return (
      <>
        {availableTransitions.slice(0, maxVisibleButtons).map((transition) => {
          const IconComponent = transition.icon;
          return (
            <Tooltip
              key={transition.action}
              title={getCompleteDisabledMessage(transition) || transition.label}
            >
              <span>
                <IconButton
                  size={size}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction(transition, e);
                  }}
                  disabled={loading || isCompleteDisabled(transition)}
                  color={transition.color || 'inherit'}
                  sx={{
                    p: 0.5,
                    '&:hover': {
                      backgroundColor: transition.color ? `${transition.color}.lighter` : 'action.hover',
                    },
                    '&.Mui-disabled': {
                      color: transition.color ? `${transition.color}.main` : 'action.disabled',
                      opacity: "0.4 !important",
                    },
                    ...sx
                  }}
                >
                  <IconComponent
                    fontSize="medium"
                  />
                </IconButton>
              </span>
            </Tooltip>
          );
        })}
      </>
    );
  }

  // Render as menu items (for dropdown menus)
  if (variant === 'menuItems') {
    return (
      <>
        {availableTransitions.map((transition) => {
          const IconComponent = transition.icon;
          return (
            <MenuItem
              key={transition.action}
              onClick={(e) => {
                e.stopPropagation();
                handleAction(transition, e);
              }}
              disabled={loading || isCompleteDisabled(transition)}
            >
              <ListItemIcon>
                <IconComponent fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {transition.label}
                {isCompleteDisabled(transition) && ` (${getCompleteDisabledMessage(transition)})`}
              </ListItemText>
            </MenuItem>
          );
        })}
      </>
    );
  }

  // Render as card buttons (for TaskCard): only the primary action. The card's
  // ⋮ menu already lists every transition, and repeating "not completed" and a
  // red "cancel" on every card in the grid was mostly noise.
  if (variant === 'card') {
    const disabledReason = getCompleteDisabledMessage(primary);
    return (
      <Box sx={sx}>
        <Tooltip title={disabledReason || ''}>
          {/* span: Tooltip needs a non-disabled child to show on a disabled button */}
          <span style={{ display: 'block' }}>
            <Button
              fullWidth
              size={size}
              variant={buttonVariant}
              startIcon={<PrimaryIcon />}
              color={primary.color || 'primary'}
              onClick={(e) => {
                e.stopPropagation();
                handleAction(primary, e);
              }}
              disabled={loading || isCompleteDisabled(primary)}
            >
              {primary.label}
            </Button>
          </span>
        </Tooltip>
      </Box>
    );
  }

  // Default: Render as full buttons (for headers)
  return (
    <Box>
      <ButtonGroup variant="contained" color={primary.color || 'primary'} sx={{ borderRadius: 2 }}>
        {/* PRIMARY ACTION */}
        <Tooltip title={getCompleteDisabledMessage(primary)}>
          <span>
            <Button
              onClick={handleMainClick}
              startIcon={<PrimaryIcon />}
              disabled={isCompleteDisabled(primary)}
              sx={{
                textTransform: "none",
                fontWeight: 500
              }}
            >
              {primary.label}
            </Button>
          </span>
        </Tooltip>

        {/* DROPDOWN TRIGGER */}
        {secondary.length > 0 && (
          <Button
            size="small"
            onClick={handleToggleMenu}
            sx={{ minWidth: 40 }}
          >
            <ArrowDropDown />
          </Button>
        )}
      </ButtonGroup>

      {/* MENU */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {/* Menu needs a flat, keyed array of children (no Fragments) */}
        {secondary.flatMap((transition) => {
          const IconComponent = transition.icon;
          const color = transition.color ? `${transition.color}.dark` : 'inherit';

          return [
            transition.action === "cancel" && <Divider key={`${transition.action}-divider`} />,
            <MenuItem
              key={transition.action}
              onClick={(e) => {
                handleAction(transition, e);
                handleClose();
              }}
              sx={{ color }}
            >
              <IconComponent style={{ marginRight: 8 }} />
              {transition.label}
            </MenuItem>,
          ].filter(Boolean);
        })}
      </Menu>
    </Box>
  );
}
